import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import './FastTrackAgentTab.css';
import './IronSightCards.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { formatCurrency, validPrice } from '../../../utils/formatting';
import InfoTip from '../../InfoTip';
import {
    PIPELINE_STEP_DURATIONS as STEP_DURATIONS, PIPELINE_THINKING_STEPS as THINKING_STEPS, PIPELINE_TIPS,
    fmtMoney, fmtPipelineDate, gapBand, groupMachineQuotes, isOtherUnitRow, meetsAiEstimate, quoteChartPoints,
    statusTone, withinTenPct,
} from '../../../utils/unitPipeline';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// ── Thinking loader ───────────────────────────────────────────────────────────

// Steps and timing shared with the full unit page (utils/unitPipeline.js).
const PIPELINE_THINKING_STEPS = THINKING_STEPS;
const PIPELINE_STEP_DURATIONS = STEP_DURATIONS;

function PipelineThinking({ unit }) {
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (step >= PIPELINE_THINKING_STEPS.length - 1) return;
        const t = setTimeout(() => setStep(s => s + 1), PIPELINE_STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [step]);

    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');

    return (
        <div className="tab-content">
            <div className="fta-thinking-card">
                <div className="fta-thinking-header">
                    <div className="fta-thinking-pulse" />
                    <div>
                        <div className="fta-thinking-title">Loading pipeline</div>
                        {unitLabel && <div className="fta-thinking-unit">{unitLabel}</div>}
                    </div>
                </div>
                <div className="fta-thinking-steps">
                    {PIPELINE_THINKING_STEPS.map((s, i) => {
                        const state = i < step ? 'done' : i === step ? 'active' : 'pending';
                        return (
                            <div key={i} className={`fta-thinking-step fta-thinking-step--${state}`}>
                                <div className="fta-thinking-step-icon">
                                    {state === 'done'
                                        ? <span className="fta-thinking-check">&#10003;</span>
                                        : state === 'active'
                                            ? <span className="fta-thinking-spinner" />
                                            : <span className="fta-thinking-dot" />}
                                </div>
                                <div className="fta-thinking-step-body">
                                    <span className="fta-thinking-step-label">{s.label}</span>
                                    {state !== 'pending' && (
                                        <span className="fta-thinking-step-detail">{s.detail}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

const ACTIVE_STATUSES = new Set([
    'new',
    'present solutions',
    'negotiation/review',
    'in stock / available',
    'pending/on order / confirmed',
    'pending',
]);

const fmt = fmtMoney;
const fmtDate = fmtPipelineDate;

// Colours per band / tone; the bands themselves live in utils/unitPipeline.js.
const GAP_COLORS = {
    up: { color: '#15803d', bg: '#15803d15' },
    near: { color: '#1d4ed8', bg: '#1d4ed815' },
    mid: { color: '#b45309', bg: '#b4530915' },
    far: { color: '#b91c1c', bg: '#b91c1c15' },
    none: { color: 'var(--text-muted)', bg: 'transparent' },
};
const getGapStyle = (gap) => {
    const { band, label } = gapBand(gap);
    return { ...GAP_COLORS[band], label };
};

const STATUS_STYLES = {
    new: { color: '#15803d', backgroundColor: '#15803d15' },
    present: { color: '#1d4ed8', backgroundColor: '#1d4ed815' },
    negotiation: { color: '#b45309', backgroundColor: '#b4530915' },
    closed: { color: '#6b7280', backgroundColor: '#6b728015' },
    other: { color: 'var(--text-secondary)', backgroundColor: 'var(--bg-tertiary)' },
};
const getStatusBadgeStyle = (status) => (status ? STATUS_STYLES[statusTone(status)] : {});

function MachineQuoteRow({ q, aiPrice, currentStockNumber }) {
    const gapStyle = getGapStyle(q.price_gap_pct);
    const statusStyle = getStatusBadgeStyle(q.quote_status);
    const aboveAi = aiPrice != null && q.selling_price != null && q.selling_price >= aiPrice;
    const isOtherUnit = isOtherUnitRow(q, currentStockNumber);

    return (
        <div style={{
            marginLeft: '0.75rem',
            marginTop: '0.5rem',
            padding: '0.55rem 0.75rem',
            background: '#fafbf7',
            borderTop: '1px solid #eceee5',
            borderRight: '1px solid #eceee5',
            borderBottom: '1px solid #eceee5',
            borderLeft: isOtherUnit ? '2px solid #e7e8e0' : '3px solid var(--sag-green, #6f8f5c)',
            borderRadius: '8px',
            opacity: isOtherUnit ? 0.8 : 1,
        }}>
            <div className="pipeline-row-main" style={{ fontSize: '0.85rem' }}>
                <div className="pipeline-row-left">
                    <span className="pipeline-customer">
                        {fmt(q.selling_price)}
                        {isOtherUnit ? (
                            <span
                                style={{ marginLeft: '0.4rem', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', padding: '1px 5px' }}
                                title={`This price is for unit #${q.unit_stock_number} in the same deal`}
                            >
                                #{q.unit_stock_number}
                            </span>
                        ) : (
                            <span
                                style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 600, color: '#5a8a1f', backgroundColor: '#6f8f5c20', borderRadius: '4px', padding: '1px 6px', letterSpacing: '0.02em' }}
                            >
                                This unit
                            </span>
                        )}
                    </span>
                    <span className="pipeline-meta">
                        {q.quote_status && (
                            <span className="bs-status-pill" style={statusStyle}>{q.quote_status}</span>
                        )}
                        {aboveAi && !isOtherUnit && (
                            <span
                                className="bs-status-pill"
                                style={{ color: '#0f766e', backgroundColor: '#0f766e15' }}
                                title={`Buyer quoted above AI estimated price of ${fmt(aiPrice)}`}
                            >
                                ≥ AI Price
                            </span>
                        )}
                        <span style={{ color: 'var(--text-muted)' }}>{fmtDate(q.created_date)}</span>
                    </span>
                </div>
                <div className="pipeline-row-right">
                    <span
                        className="last-quote-badge"
                        style={{ color: gapStyle.color, backgroundColor: gapStyle.bg }}
                        title={`Gap vs asking price: ${gapStyle.label}`}
                    >
                        {gapStyle.label}
                    </span>
                </div>
            </div>
            {q.hours != null && q.hours > 0 && (
                <div className="bs-quote-detail-row" style={{ marginTop: '0.25rem' }}>
                    <span className="bs-detail-chip">{q.hours.toLocaleString('en-US')} hrs</span>
                </div>
            )}
        </div>
    );
}

MachineQuoteRow.propTypes = {
    q: PropTypes.object.isRequired,
    aiPrice: PropTypes.number,
    currentStockNumber: PropTypes.string,
};

MachineQuoteRow.defaultProps = {
    aiPrice: null,
    currentStockNumber: null,
};

function QuotePriceChart({ quotes, currentStockNumber, aiPrice, askingPrice, openQuoteIds }) {
    // Only current unit's rows, sorted oldest → newest
    const points = quoteChartPoints(quotes, currentStockNumber, { open: [...openQuoteIds].map((id) => ({ quote_id: id })) });
    if (!points) return null;
    const rows = points.map((p) => p.row);

    const labels = rows.map(r =>
        new Date(r.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    );
    const prices = rows.map(r => r.selling_price);

    const pointColors = rows.map(r =>
        openQuoteIds.has(r.quote_id) ? '#15803d' : '#9ca3af'
    );
    const pointBorderColors = rows.map(r =>
        openQuoteIds.has(r.quote_id) ? '#15803d' : '#6b7280'
    );

    const datasets = [
        {
            label: 'Quoted Price',
            data: prices,
            borderColor: '#1d4ed8',
            backgroundColor: '#1d4ed820',
            pointBackgroundColor: pointColors,
            pointBorderColor: pointBorderColors,
            pointRadius: 5,
            tension: 0.3,
            fill: false,
        },
    ];

    if (aiPrice != null) {
        datasets.push({
            label: 'AI Est. Price',
            data: Array(labels.length).fill(aiPrice),
            borderColor: '#0f766e',
            borderDash: [5, 4],
            pointRadius: 0,
            tension: 0,
            fill: false,
        });
    }

    if (askingPrice != null) {
        datasets.push({
            label: 'Asking Price',
            data: Array(labels.length).fill(askingPrice),
            borderColor: '#b45309',
            borderDash: [3, 3],
            pointRadius: 0,
            tension: 0,
            fill: false,
        });
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { font: { size: 11 }, boxWidth: 16, padding: 12 },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(ctx.parsed.y);
                        if (ctx.dataset.label === 'Quoted Price') {
                            const row = rows[ctx.dataIndex];
                            const active = openQuoteIds.has(row?.quote_id);
                            return `${price}  ·  ${active ? 'Active' : 'Closed'}`;
                        }
                        return `${ctx.dataset.label}: ${price}`;
                    },
                },
            },
        },
        scales: {
            x: { ticks: { font: { size: 10 }, maxRotation: 30 } },
            y: {
                ticks: {
                    font: { size: 10 },
                    callback: (v) => `$${(v / 1000).toFixed(0)}k`,
                },
            },
        },
    };

    return (
        <div style={{ marginTop: '1rem' }}>
            <div style={{ height: '220px' }}>
                <Line data={{ labels, datasets }} options={options} />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--text-muted)', justifyContent: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#15803d', display: 'inline-block' }} />
                    Active quote
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9ca3af', display: 'inline-block' }} />
                    Closed quote
                </span>
            </div>
        </div>
    );
}

QuotePriceChart.propTypes = {
    quotes: PropTypes.array,
    currentStockNumber: PropTypes.string,
    aiPrice: PropTypes.number,
    askingPrice: PropTypes.number,
    openQuoteIds: PropTypes.instanceOf(Set),
};

QuotePriceChart.defaultProps = {
    quotes: [],
    currentStockNumber: null,
    aiPrice: null,
    askingPrice: null,
    openQuoteIds: new Set(),
};

function TradeInList({ tradeIns }) {
    const [open, setOpen] = useState(false);
    if (!tradeIns || tradeIns.length === 0) return null;

    return (
        <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.5rem' }}>
            <button
                onClick={() => setOpen((v) => !v)}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: '#b45309',
                    padding: '0 0 0 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
            >
                <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                    <path d="M8 1a2 2 0 0 1 2 2v1h1.5A1.5 1.5 0 0 1 13 5.5v7A1.5 1.5 0 0 1 11.5 14h-7A1.5 1.5 0 0 1 3 12.5v-7A1.5 1.5 0 0 1 4.5 4H6V3a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v1h2V3a1 1 0 0 0-1-1zM4.5 5a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.5-.5H4.5z"/>
                </svg>
                {open ? '▲' : '▼'} Trade-ins ({tradeIns.length})
            </button>

            {open && (
                <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingLeft: '0.75rem' }}>
                    {tradeIns.map((ti) => {
                        const title = [ti.year, ti.make, ti.model].filter(Boolean).join(' ') || 'Unknown unit';
                        const hasOverallowance = ti.overallowance != null && ti.overallowance > 0;
                        return (
                            <div
                                key={ti.trade_in_id}
                                style={{
                                    borderLeft: '3px solid #b45309',
                                    paddingLeft: '0.65rem',
                                    background: '#b4530908',
                                    borderRadius: '0 4px 4px 0',
                                    padding: '0.4rem 0.6rem',
                                }}
                            >
                                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>
                                    {title}
                                </div>
                                <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem' }}>
                                    {ti.serial_number && (
                                        <span>SN: {ti.serial_number}</span>
                                    )}
                                    {ti.hours != null && (
                                        <span>{ti.hours.toLocaleString('en-US')} hrs</span>
                                    )}
                                    {ti.dealer_stock_number && (
                                        <span>Stock #{ti.dealer_stock_number}</span>
                                    )}
                                </div>
                                <div style={{ marginTop: '0.35rem', fontSize: '0.76rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.1rem' }}>
                                    {ti.base_retail_value != null && (
                                        <span>
                                            <span style={{ color: 'var(--text-muted)' }}>Base retail </span>
                                            <span style={{ fontWeight: 600 }}>{fmt(ti.base_retail_value)}</span>
                                        </span>
                                    )}
                                    {hasOverallowance && (
                                        <span>
                                            <span style={{ color: 'var(--text-muted)' }}>Over-allowance </span>
                                            <span style={{ fontWeight: 600, color: '#b91c1c' }}>
                                                {fmt(ti.overallowance)}
                                                {' '}
                                                <span title={PIPELINE_TIPS.overallowance}>⚠</span>
                                            </span>
                                        </span>
                                    )}
                                    {ti.net_trade_value != null && (
                                        <span>
                                            <span style={{ color: 'var(--text-muted)' }}>Net trade </span>
                                            <span style={{ fontWeight: 600 }}>{fmt(ti.net_trade_value)}</span>
                                        </span>
                                    )}
                                    {ti.payoff_amount != null && ti.payoff_amount > 0 && (
                                        <span>
                                            <span style={{ color: 'var(--text-muted)' }}>Payoff </span>
                                            <span style={{ fontWeight: 600 }}>{fmt(ti.payoff_amount)}</span>
                                        </span>
                                    )}
                                    {ti.trade_value_after_payoff != null && ti.payoff_amount != null && ti.payoff_amount > 0 && (
                                        <span>
                                            <span style={{ color: 'var(--text-muted)' }}>After payoff </span>
                                            <span style={{ fontWeight: 600 }}>{fmt(ti.trade_value_after_payoff)}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

TradeInList.propTypes = {
    tradeIns: PropTypes.array,
};

TradeInList.defaultProps = {
    tradeIns: [],
};

function PipelineTab({ unit, pipeline, pipelineLoading, quoteEquipment, quoteEquipmentLoading }) {
    const [expandedQuotes, setExpandedQuotes] = useState({});

    const aiPrice = validPrice(unit?.PredictedPrice);

    // Per-machine quotes by deal, and the orphans ("Other Quotes").
    const { byQuoteId: qeByQuoteId, orphans: orphanQe } = groupMachineQuotes(pipeline, quoteEquipment);

    const loading = pipelineLoading || quoteEquipmentLoading;

    if (loading) {
        return <PipelineThinking unit={unit} />;
    }

    return (
        <div className="tab-content">
            {(
                <>
                    {/* Per-machine price summary */}
                    {quoteEquipment && quoteEquipment.total_quotes > 0 && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="section-title is-section-title">
                                Price Intelligence
                                <InfoTip text={PIPELINE_TIPS.priceIntel} />
                            </h3>
                            <div className="buyer-signals-summary">
                                <div className="bs-stat">
                                    <span className="bs-stat-label">Asking Price</span>
                                    <span className="bs-stat-value">{fmt(quoteEquipment.asking_price)}</span>
                                </div>
                                {aiPrice != null && (
                                    <div className="bs-stat">
                                        <span className="bs-stat-label">
                                            AI Est. Price
                                            <InfoTip text={PIPELINE_TIPS.aiEst} />
                                        </span>
                                        <span className="bs-stat-value" style={{ color: '#0f766e' }}>{fmt(aiPrice)}</span>
                                    </div>
                                )}
                                <div className="bs-stat">
                                    <span className="bs-stat-label">
                                        Avg Quoted
                                        <InfoTip text={PIPELINE_TIPS.avgQuoted} />
                                    </span>
                                    <span className="bs-stat-value">{fmt(quoteEquipment.avg_quoted_price)}</span>
                                </div>
                                <div className="bs-stat">
                                    <span className="bs-stat-label">
                                        Avg Gap
                                        <InfoTip text={PIPELINE_TIPS.avgGap} />
                                    </span>
                                    <span className="bs-stat-value" style={{ color: getGapStyle(quoteEquipment.avg_price_gap_pct).color }}>
                                        {getGapStyle(quoteEquipment.avg_price_gap_pct).label}
                                    </span>
                                </div>
                                <div className="bs-stat">
                                    <span className="bs-stat-label">High / Low</span>
                                    <span className="bs-stat-value">
                                        {fmt(quoteEquipment.max_quoted_price)} / {fmt(quoteEquipment.min_quoted_price)}
                                    </span>
                                </div>
                            </div>
                            <QuotePriceChart
                                quotes={quoteEquipment.quotes}
                                currentStockNumber={String(unit.StockNumber)}
                                aiPrice={aiPrice}
                                askingPrice={quoteEquipment.asking_price}
                                openQuoteIds={new Set((pipeline?.open ?? []).map(q => q.quote_id))}
                            />

                            {withinTenPct(quoteEquipment.avg_price_gap_pct) && (
                                <div className="pipeline-qualifying-banner" style={{ marginTop: 'var(--spacing-sm)' }}>
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                    </svg>
                                    Buyer quoted within 10% of ask — call before dropping list price
                                </div>
                            )}
                            </div>
                        </section>
                    )}

                    {/* Open deals — per-machine quotes nested inside each deal */}
                    {pipeline && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="section-title is-section-title">
                                Open Deals ({pipeline.open.length})
                                <InfoTip text={PIPELINE_TIPS.openDeals} />
                            </h3>
                            {pipeline.open.length === 0 ? (
                                <p className="pipeline-empty">No open deals</p>
                            ) : (
                                <div className="pipeline-list">
                                    {pipeline.open.map((q) => {
                                        const meetsAI = meetsAiEstimate(q, aiPrice);
                                        const machineQuotes = qeByQuoteId[q.quote_id] ?? [];
                                        return (
                                            <div key={q.quote_id} className={`pipeline-row${meetsAI ? ' pipeline-row-qualifying' : ''}`}>
                                                {meetsAI && (
                                                    <div className="pipeline-qualifying-banner">
                                                        <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                                        </svg>
                                                        Quote meets AI estimate ({formatCurrency(aiPrice)}) — retarget this customer
                                                    </div>
                                                )}
                                                <div className="pipeline-row-main">
                                                    <div className="pipeline-row-left">
                                                        <span className="pipeline-customer">
                                                            {q.customer || '—'}
                                                            {q.status && (
                                                                <span
                                                                    className="bs-status-pill"
                                                                    style={getStatusBadgeStyle(q.status)}
                                                                >
                                                                    {q.status}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className="pipeline-meta">
                                                            {[q.salesperson, q.store].filter(Boolean).join(' · ')}
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-row-right">
                                                        <span className={`pipeline-amount${meetsAI ? ' pipeline-amount-qualifying' : ''}`}>
                                                            {q.quote_total
                                                                ? `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(q.quote_total)}${q.is_multi_unit ? ` (${q.unit_count} units)` : ''}`
                                                                : '—'}
                                                        </span>
                                                        <span className="pipeline-days">
                                                            {q.days_ago != null ? `${q.days_ago} days ago` : '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {q.is_multi_unit && (
                                                    <div className="pipeline-multiunit">
                                                        <button
                                                            className="pipeline-expand-btn"
                                                            onClick={() => setExpandedQuotes((prev) => ({ ...prev, [q.quote_id]: !prev[q.quote_id] }))}
                                                        >
                                                            {expandedQuotes[q.quote_id] ? '▲ Hide offer' : '▼ View full offer'}
                                                        </button>
                                                        {expandedQuotes[q.quote_id] && (
                                                            <div className="pipeline-units">
                                                                {q.units.map((u) => (
                                                                    <div key={u.stock_number} className="pipeline-unit-row">
                                                                        <span className="pipeline-unit-stock">#{u.stock_number}</span>
                                                                        <span className="pipeline-unit-detail">
                                                                            {[u.make, u.model, u.year].filter(Boolean).join(' ')}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Per-machine quotes linked to this deal */}
                                                {machineQuotes.map((mq, i) => (
                                                    <MachineQuoteRow key={mq.quote_equipment_id || i} q={mq} aiPrice={aiPrice} currentStockNumber={unit.StockNumber} />
                                                ))}
                                                {/* Trade-ins on this deal */}
                                                <TradeInList tradeIns={q.trade_ins} />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            </div>
                        </section>
                    )}

                    {/* Orphan per-machine quotes not linked to any known deal */}
                    {orphanQe.length > 0 && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="section-title is-section-title" style={{ color: 'var(--text-muted)' }}>
                                Other Quotes ({orphanQe.length})
                            </h3>
                            <div className="pipeline-list" style={{ opacity: 0.7 }}>
                                {orphanQe.map((q, i) => (
                                    <div key={q.quote_equipment_id || i} className="pipeline-row">
                                        <MachineQuoteRow q={q} aiPrice={aiPrice} />
                                    </div>
                                ))}
                            </div>
                            </div>
                        </section>
                    )}

                    {/* Lost deals */}
                    {pipeline && pipeline.lost.length > 0 && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="section-title pipeline-lost-title is-section-title" style={{ color: '#b91c1c' }}>
                                Closed Deals ({pipeline.lost.length})
                                <InfoTip text={PIPELINE_TIPS.closedDeals} />
                            </h3>
                            <div className="pipeline-lost-list">
                                {pipeline.lost.map((q) => {
                                    const machineQuotes = qeByQuoteId[q.quote_id] ?? [];
                                    return (
                                        <div key={q.quote_id}>
                                            <div className="pipeline-row" style={{ opacity: 0.8 }}>
                                                <div className="pipeline-row-main">
                                                    <div className="pipeline-row-left">
                                                        <span className="pipeline-customer">
                                                            {q.customer || '—'}
                                                            <span className="bs-status-pill" style={getStatusBadgeStyle(q.status || 'no sale')}>
                                                                {q.status || 'Closed'}
                                                            </span>
                                                        </span>
                                                        <span className="pipeline-meta">
                                                            {q.salesperson || ''}
                                                            {q.lost_reason && (
                                                                <span style={{ color: 'var(--text-muted)' }}> · {q.lost_reason}</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-row-right">
                                                        {q.lost_amount != null && (
                                                            <span className="pipeline-amount">
                                                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(q.lost_amount)}
                                                            </span>
                                                        )}
                                                        <span className="pipeline-days">{q.lost_date || ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {machineQuotes.map((mq, i) => (
                                                <MachineQuoteRow key={mq.quote_equipment_id || i} q={mq} aiPrice={aiPrice} currentStockNumber={unit.StockNumber} />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                            </div>
                        </section>
                    )}

                    {!pipeline && (!quoteEquipment || quoteEquipment.total_quotes === 0) && (
                        <p className="pipeline-empty" style={{ marginTop: '2rem' }}>No pipeline or quote data for this unit.</p>
                    )}
                </>
            )}
        </div>
    );
}

PipelineTab.propTypes = {
    unit: PropTypes.object.isRequired,
    pipeline: PropTypes.shape({
        open: PropTypes.array,
        lost: PropTypes.array,
    }),
    pipelineLoading: PropTypes.bool,
    quoteEquipment: PropTypes.shape({
        asking_price: PropTypes.number,
        quotes: PropTypes.array,
        total_quotes: PropTypes.number,
        avg_quoted_price: PropTypes.number,
        max_quoted_price: PropTypes.number,
        min_quoted_price: PropTypes.number,
        avg_price_gap_pct: PropTypes.number,
    }),
    quoteEquipmentLoading: PropTypes.bool,
};

PipelineTab.defaultProps = {
    pipeline: null,
    pipelineLoading: false,
    quoteEquipment: null,
    quoteEquipmentLoading: false,
};

export default PipelineTab;
